/**
 * DISPATCH — PREPARE AND APPROVE (item 4).
 *
 * The yard screen, in CTS 1's shape: the load is built on the left, one
 * scanned serial at a time, and the queue fills on the right. What CTS 2 adds
 * is the ORDER (ADR-012): a load is prepared from an approved order, each
 * cylinder is put against the line it fills, and a load with no order behind it
 * has to say which kind of exception it is and why, in writing (DEF-106).
 *
 * Nothing moves here. A prepared challan is a piece of paper waiting for the
 * person who checks it; the cylinders stay where they are until it is approved,
 * and they stay at the plant until it departs.
 */
import * as React from 'react';
import { useState, useEffect } from 'react';
import { api, can, type Session } from '../api.js';
import { useAsk, useRun, go, routeQuery } from '../hooks.js';
import { Banner, Button, Card, Count, Loading, Money, Pill, Register, ScanBox, Select, Serial, Text, TextArea, type Column } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { SendOne } from './departure.js';
import { ConfirmOne } from './delivery.js';
import { CustomerPickerById } from '../pickers.js';
import { NextStep } from '../next.js';
import { challanStanding, actionWords } from '../standing.js';
import { count, date, rupeesToPaise, today, when, words, qty as qtyText } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('dispatch')!.messages;

/** Approval writes journey = 'approved'; the truck has not moved until it is 'departed', and 'closed' is delivered (D1, 22 Sep). */
const atPlant = (journey: string | null) => !journey || journey === 'approved';

export function Dispatch({ id, session }: { id: string | null; session: Session }) {
  if (id === 'new') return <PrepareDispatch session={session} />;
  if (id) return <OneChallan challanId={Number(id)} session={session} />;
  return <DispatchRegister session={session} />;
}

function DispatchRegister({ session }: { session: Session }) {
  const [status, setStatus] = useState<'pending' | 'approved' | 'rejected' | ''>('pending');
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [status]);
  const asked = useAsk(() => api.view('challans.list', { type: 'dispatch', ...(status ? { status } : {}), page, pageSize: 50 }), [status, page], MESSAGES);

  const columns: readonly Column<{
    challanId: number; number: string | null; customerName: string; siteName: string; eventDate: string;
    status: string; journey: string | null; lineCount: number; orderId: number | null; withoutOrderReason: string | null; vehicle: string | null;
  }>[] = [
    { key: 'number', head: 'Challan', cell: (r) => r.number ? <Serial>{r.number}</Serial> : <span className="muted">not issued</span> },
    { key: 'customer', head: 'Customer', cell: (r) => <><strong>{r.customerName}</strong><div className="muted">{r.siteName}</div></> },
    { key: 'date', head: 'Date', cell: (r) => date(r.eventDate) },
    { key: 'cyl', head: 'Cylinders', right: true, cell: (r) => <Count n={r.lineCount} /> },
    { key: 'order', head: 'Behind it', cell: (r) => r.orderId
        ? <a href={`#/orders/${r.orderId}`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/orders/${r.orderId}`); }}>order #{r.orderId}</a>
        : <Pill tone="empty">{r.withoutOrderReason ?? 'no order'}</Pill> },
    { key: 'status', head: 'Where it stands', cell: (r) => { const s = challanStanding('dispatch', r.status, r.journey); return <Pill tone={s.tone}>{s.word}</Pill>; } },
  ];

  return (
    <Page title="Dispatch" sub="Loads being prepared, checked and sent."
          actions={can(session, 'challan.create') ? <Button kind="primary" onClick={() => go('#/dispatch/new')}>New dispatch</Button> : null}>
      <div className="toolbar">
        <Select name="status" label="Status" value={status} onChange={(v) => setStatus(v as 'pending' | 'approved' | 'rejected' | '')} placeholder="Any status"
                // R20261005-07 — the filter and the rows say the same thing: this offered
                // "Approved" while the rows it filtered read "Ready to leave", so the operator
                // had to know they were the same stage.
                options={[{ value: 'pending', label: 'Being checked' }, { value: 'approved', label: 'Ready to leave' },
                          { value: 'rejected', label: 'Sent back for correction' }]} />
      </div>
      <Card>
        {asked.loading ? <Loading what="challans" /> : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
          : (<>
              <Register columns={columns} rows={asked.data!.rows} empty="No load matches that."
                        onOpen={(r) => go(`#/dispatch/${r.challanId}`)}
 />
              <Pager page={asked.data!.page} onPage={setPage} />
            </>)}
      </Card>
    </Page>
  );
}

/** 10 Oct — THE JOURNEY, as steps. The next step is a form on this page, not a link to another screen. */
function Journey({ type, status, journey }: { type: string; status: string; journey: string | null }) {
  const steps = type === 'dispatch'
    ? ['Prepared', 'Approved', 'Departed', 'Delivered', 'Returned']
    : ['Received', 'Approved'];
  const at = status === 'rejected' || status === 'reversed' ? -1
    : status === 'pending' ? 0
    : type !== 'dispatch' ? 1
    : journey === 'departed' ? 2
    : journey === 'delivered' ? 3
    : journey === 'closed' ? 4 : 1;
  return (
    <ol className="journey" aria-label="Where this challan is on its journey">
      {steps.map((label, i) => (
        <li key={label} className={i < at ? 'done' : i === at ? 'now' : ''}><span className="journey-dot" aria-hidden="true" />{label}</li>
      ))}
      {at < 0 ? <li className="off">{status === 'rejected' ? 'Rejected' : 'Reversed'}</li> : null}
    </ol>
  );
}

export function OneChallan({ challanId, session }: { challanId: number; session: Session }) {
  const asked = useAsk(() => api.view('challans.get', { challanId }), [challanId], MESSAGES);
  const runner = useRun(MESSAGES);
  const [rejecting, setRejecting] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [reverseReason, setReverseReason] = useState('');
  const [correcting, setCorrecting] = useState(false);
  const [charging, setCharging] = useState(false);
  const [reason, setReason] = useState('');

  if (asked.loading) return <Page title="Load"><Loading what="the load" /></Page>;
  if (asked.error) return <Page title="Load"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Page>;
  const d = asked.data!;
  const c = d.challan;

  async function reverse() {
    const ok = await runner.run((retryKey) => api.command('challan.reverse',
      { challanId, reason: reverseReason.trim() }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setReversing(false); setReverseReason(''); asked.reload(); }
  }

  async function approve() {
    const ok = await runner.run((retryKey) => api.command('challan.approve', { challanId }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) asked.reload();
  }
  async function reject() {
    const ok = await runner.run(() => api.command('challan.reject', { challanId, reason: reason.trim() }));
    if (ok) { setRejecting(false); asked.reload(); }
  }

  return (
    <Page title={c.number ? `Challan ${c.number}` : `Load for ${c.customerName}`}
          sub={`${c.customerName} · ${c.siteName} · ${date(c.eventDate)}`}
          actions={<>
            {c.status === 'pending' && can(session, 'challan.edit_pending') ? (
              <>
                <Button onClick={() => { setCorrecting((v) => !v); setCharging(false); }}>{correcting ? 'Leave the load' : 'Correct the load'}</Button>
                <Button onClick={() => { setCharging((v) => !v); setCorrecting(false); }}>{charging ? 'No charge' : 'Add a charge'}</Button>
              </>
            ) : null}
            {c.status === 'pending' && can(session, 'challan.approve') ? (
              <>
                <Button kind="primary" onClick={() => { void approve(); }} disabled={runner.busy}>{runner.busy ? 'Approving…' : 'Approve the load'}</Button>
                <Button kind="danger" onClick={() => setRejecting((v) => !v)}>{rejecting ? 'Keep it' : 'Reject'}</Button>
              </>
            ) : null}
            {c.status === 'approved' && atPlant(c.journey) && c.type === 'dispatch' && can(session, 'dispatch.depart')
              ? <Button kind="primary" onClick={() => (document.getElementById('departed-on') as { focus?: () => void } | null)?.focus?.()}>Record departure</Button> : null}
            {c.status === 'approved' && c.journey === 'departed' && can(session, 'delivery.confirm')
              ? <Button kind="primary" onClick={() => (document.querySelector('#delivered-on, [name=\"delivered-on\"], .confirm-delivery input') as { focus?: () => void } | null)?.focus?.()}>Confirm delivery</Button> : null}
            {c.status === 'approved' && (c.journey === 'delivered' || c.journey === 'closed') && c.type === 'dispatch' && can(session, 'challan.create')
              ? <Button kind="good" onClick={() => go(`#/move?dir=in&customer=${c.customerId}`)}>Receive the empties back</Button> : null}
            {c.status === 'approved' && can(session, 'challan.reverse')
              ? <Button kind="danger" onClick={() => setReversing((v) => !v)}>{reversing ? 'Let it stand' : 'Reverse'}</Button> : null}
          </>}>

      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} {...(runner.error.reference ? { reference: runner.error.reference } : {})} /> : null}

      <Journey type={c.type} status={c.status} journey={c.journey} />

      {c.status === 'pending' ? (
        <NextStep title="Prepared, being checked. Nothing has moved yet."
                  detail={can(session, 'challan.approve') ? 'Approve it to let it leave, or reject it in writing so the yard can put it right.' : 'Somebody with the approval function checks it; then the truck can leave.'} />
      ) : c.status === 'rejected' ? (
        <NextStep kind="warn" title="Rejected — the cylinders stayed where they were." {...(c.rejectReason ? { detail: c.rejectReason } : {})}
                  {...(can(session, 'challan.create') ? { action: { label: 'Prepare it again', href: c.orderId ? `#/dispatch/new?order=${c.orderId}` : '#/dispatch/new' } } : {})} />
      ) : c.status === 'approved' && atPlant(c.journey) && c.type === 'dispatch' ? (
        can(session, 'dispatch.depart')
          ? <SendOne challanId={challanId} session={session} embedded onChanged={() => asked.reload()} />
          : <NextStep kind="good" title="Approved and standing at the plant." detail="Somebody with the departure function records the truck leaving; that issues the challan and the delivery memo." />
      ) : c.status === 'approved' && c.journey === 'departed' ? (
        <>
          <NextStep kind="good" title={`This load left on ${date(c.departedOn)}.`} detail={can(session, 'delivery.confirm') ? 'Confirm the delivery below when the truck reaches the customer\'s gate.' : 'Somebody with the delivery function confirms it at the customer\'s gate.'} />
          {can(session, 'delivery.confirm') ? <ConfirmOne challanId={challanId} session={session} embedded onChanged={() => asked.reload()} /> : null}
        </>
      ) : c.status === 'approved' && (c.journey === 'delivered' || c.journey === 'closed') ? (
        <NextStep kind="good" title="This delivery is complete." detail="The holding is on the customer's account; the empties come back on a return challan."
                  secondary={{ label: 'The customer\'s account', href: `#/customers/${c.customerId}?tab=money` }} />
      ) : null}

      {c.status === 'reversed' ? (
        <Banner kind="warn" title="This challan was REVERSED — it should never have been entered."
                {...(c.reverseReason ? { detail: `${c.reverseReason} — the papers below stay exactly as issued; the reversal is its own recorded action.` } : {})} />
      ) : null}

      {reversing ? (
        <Card title="Reverse this challan" sub="the papers stay exactly as issued; custody and money are compensated by a NEW action, in writing">
          <TextArea name="reverse-reason" label="Why this challan should never have been entered" value={reverseReason} onChange={setReverseReason} />
          <Button kind="danger" onClick={() => { void reverse(); }} disabled={runner.busy || !reverseReason.trim()}>
            {runner.busy ? 'Reversing…' : 'Reverse it — custody and money come back'}
          </Button>
        </Card>
      ) : null}

      {c.withoutOrderReason ? (
        <Banner kind="warn" title={`This load has no order behind it — recorded as ${words(c.withoutOrderReason)}.`} {...(c.withoutOrderNote ? { detail: c.withoutOrderNote } : {})} />
      ) : null}

      {correcting ? <CorrectTheLoad challanId={challanId} lines={d.lines} orderId={c.orderId}
                                    onDone={() => { setCorrecting(false); asked.reload(); }} /> : null}
      {charging ? <AddCharge challanId={challanId} onDone={() => { setCharging(false); asked.reload(); }} /> : null}

      {rejecting ? (
        <Card title="Reject this load" sub="say what is wrong with it; the cylinders stay where they are">
          <TextArea name="reject-reason" label="Why it is rejected" value={reason} onChange={setReason} />
          <Button kind="danger" onClick={() => { void reject(); }} disabled={runner.busy || !reason.trim()}>Reject the load</Button>
        </Card>
      ) : null}

      <div className="grid two">
        <Card title="The load">
          <div className="detail-grid">
            <div><div className="k">Where it stands</div><div className="v">{(() => { const s = challanStanding(c.type, c.status, c.journey); return <Pill tone={s.tone}>{s.word}</Pill>; })()}</div></div>
            {c.legacyNumber ? <div><div className="k">Old register number</div><div className="v">{c.legacyNumber}</div></div> : null}
            <div><div className="k">Cylinders</div><div className="v">{count(c.lineCount)}</div></div>
            <div><div className="k">Delivery</div><div className="v">{words(c.deliveryMode)}</div></div>
            <div><div className="k">Vehicle</div><div className="v">{c.vehicle ?? '—'}{c.driver ? ` · ${c.driver}` : ''}</div></div>
            <div><div className="k">Departed</div><div className="v">{date(c.departedOn)}</div></div>
            <div><div className="k">Order</div><div className="v">{c.orderId ? <a href={`#/orders/${c.orderId}`}>order #{c.orderId}</a> : 'none'}</div></div>
            <div><div className="k">Customer</div><div className="v"><a href={`#/customers/${c.customerId}`}>{c.customerName}</a></div></div>
            <div><div className="k">Account</div><div className="v"><a href={`#/account/${c.customerId}`}>holdings and money</a></div></div>
          </div>
        </Card>
        {d.pendingCharges.length ? (
          <Card title="What this load will be charged" sub="added while checking — these reach the delivery memo the departure raises">
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {d.pendingCharges.map((c) => (
                <li key={c.chargeId}>{c.description} <span className="muted">· {words(c.chargeType)}{c.qty ? ` · ${c.qty}` : ''}</span> — <Money paise={c.amountPaise} plain /></li>
              ))}
            </ul>
          </Card>
        ) : null}

        <Card title="Papers" sub="printed when the truck leaves, and never redrawn">
          {d.documents.length === 0 ? <p className="muted">No document has been issued for this load yet.</p> : (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {d.documents.map((doc) => (
                <li key={doc.id}>
                  <a href={api.documentUrl(doc.id)} target="_blank" rel="noreferrer">{words(doc.kind)} {doc.number}</a>
                  <span className="muted"> · version {doc.version}</span>
                </li>
              ))}
            </ul>
          )}
          {d.memo ? <p style={{ marginBottom: 0 }}>Delivery memo <strong>{d.memo.number ?? `#${d.memo.billId}`}</strong> — <Money paise={d.memo.totalPaise} /> <Pill>{d.memo.status}</Pill></p> : null}
        </Card>
      </div>

      <Card title="What is on it">
        <Register
          columns={[
            { key: 'cyl', head: 'Cylinder', cell: (l: { cylinderId: number; internalId: string }) => <a href={`#/cylinders/${l.cylinderId}`}><Serial>{l.internalId}</Serial></a> },
            { key: 'serial', head: 'Serial', cell: (l: { serialNo: string }) => l.serialNo },
            { key: 'gas', head: 'Gas', cell: (l: { gasService: string }) => words(l.gasService) },
            { key: 'state', head: 'Now', cell: (l: { currentState: string | null; nowAt: string | null }) => <Pill>{l.nowAt ?? l.currentState}</Pill> },
            { key: 'outcome', head: 'Delivery', cell: (l: { deliveryOutcome: string | null; outcomeReason: string | null }) => l.deliveryOutcome ? <><Pill>{l.deliveryOutcome}</Pill>{l.outcomeReason ? <div className="muted">{l.outcomeReason}</div> : null}</> : <span className="muted">—</span> },
          ]}
          rows={d.lines} empty="Nothing is on this load." />
      </Card>

      <Card title="What happened to it" sub="who did what, when, and why">
        <ul className="timeline">
          {d.audit.length === 0 ? <p className="muted" style={{ margin: 0 }}>Nothing has been recorded since it was entered.</p> : null}
          {d.audit.map((a) => (
            <li key={a.auditId}>
              <strong>{actionWords(a.action)}</strong> by {a.actorName ?? `user ${a.actorId}`}
              {a.reason ? <> — {a.reason}</> : null}
              <div className="when">{when(a.at)}</div>
            </li>
          ))}
        </ul>
      </Card>
    </Page>
  );
}

/**
 * THE APPROVER'S CORRECTION (DEF-123). The man who checks the load is the one
 * who finds that the yard put the wrong cylinder on it, and he must be able to
 * take it off — before he approves, never after. Nothing has moved yet, so the
 * whole list is simply restated.
 */
function CorrectTheLoad({ challanId, lines, orderId, onDone }: {
  challanId: number;
  lines: readonly { cylinderId: number; internalId: string; serialNo: string; gasService: string; orderLineId: number | null }[];
  orderId: number | null;
  onDone: () => void;
}) {
  const [kept, setKept] = useState(() => lines.map((l) => ({
    cylinderId: l.cylinderId, internalId: l.internalId, serialNo: l.serialNo, gasService: l.gasService,
    // a challan line does not carry the shell's size, so the chooser matches on gas alone here
    waterCapacityL: null as string | null,
    ...(l.orderLineId ? { orderLineId: l.orderLineId } : {}),
  })));
  const [serial, setSerial] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  const runner = useRun(MESSAGES);
  const order = useAsk(() => (orderId ? api.view('orders.get', { orderId }) : Promise.resolve(null)), [orderId], MESSAGES);

  async function add() {
    const typed = serial.trim();
    if (!typed) return;
    setScanError(null);
    try {
      // IDENTITY IS THE SERVER'S ANSWER, BY EQUALITY, OVER THE WHOLE REGISTER.
      // This asked the SUBSTRING search for five rows and, when none of them
      // matched exactly, took `?? found.rows[0]` — a different cylinder, put
      // on the challan silently. It also could not tell "no such cylinder"
      // from "the exact one was not in the five I asked for", because that
      // page is ordered by internal id, not by how well a row answers.
      const r = await api.view('cylinders.resolve', { token: typed });
      if (r.total === 0) { setScanError(`No cylinder answers to “${typed}”.`); return; }
      if (r.total > 1) {
        setScanError(r.collision
          ? `“${typed}” is one cylinder's own number and another's sticker — add it from the yard screen, where you can pick between them.`
          : `“${typed}” is on ${r.total} different cylinders — add it from the yard screen, where you can pick between them.`);
        return;
      }
      const hit = r.candidates[0]!;
      if (kept.some((k) => k.cylinderId === hit.cylinderId)) { setScanError(`${hit.internalId} is already on this load.`); return; }
      if (hit.state !== 'filled_in_plant') { setScanError(`${hit.internalId} is ${words(hit.state)} — only a filled cylinder in the plant can go out.`); return; }
      const fits = linesThatFit(order.data?.lines, hit);
      if (orderId && fits.length === 0) { setScanError(`${hit.internalId} fits no line of this order that still has anything outstanding — check the gas and the size.`); return; }
      const line = fits.length === 1 ? fits[0] : undefined;
      setKept((old) => [...old, { cylinderId: hit.cylinderId, internalId: hit.internalId, serialNo: hit.serialNo, gasService: hit.gasService,
                                  waterCapacityL: hit.waterCapacityL, ...(line ? { orderLineId: line.orderLineId } : {}) }]);
      setSerial('');
    } catch (e) {
      setScanError(e instanceof Error ? e.message : String(e));
    }
  }

  async function submit() {
    const ok = await runner.run(() => api.command('challan.editPendingLines', {
      challanId, lines: kept.map((k) => ({ cylinderId: k.cylinderId, ...(k.orderLineId ? { orderLineId: k.orderLineId } : {}) })),
    }));
    if (ok) onDone();
  }

  const unchanged = kept.length === lines.length && kept.every((k, i) => k.cylinderId === lines[i]!.cylinderId);

  return (
    <Card title="Correct the load" sub="nothing has moved yet — what leaves is whatever is listed here when it is approved">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {scanError ? <Banner kind="warn" title={scanError} /> : null}
      {kept.length === 0 ? <Banner kind="warn" title="A load with nothing on it cannot be sent." detail="Put a cylinder back, or reject the load instead." /> : null}
      {kept.map((k) => (
        <div className="queue-item" key={k.cylinderId}>
          <span className="grow"><Serial>{k.internalId}</Serial> <span className="muted">{k.serialNo} · {words(k.gasService)}</span></span>
          {orderId ? (() => {
            const fits = linesThatFit(order.data?.lines, k);
            return fits.length > 1 || (!k.orderLineId && fits.length > 0)
              ? <select value={k.orderLineId ?? ''} aria-label="Which order line this cylinder fills"
                        onChange={(e: { target: { value: string } }) => setKept((old) => old.map((x) => { if (x.cylinderId !== k.cylinderId) return x; const { orderLineId: _was, ...rest } = x; const v = Number(e.target.value); return v ? { ...rest, orderLineId: v } : rest; }))}>
                  <option value="">Which line?</option>
                  {fits.map((l) => <option key={l.orderLineId} value={l.orderLineId}>line #{l.lineNo} · {words(l.gasService)}{l.waterCapacityL ? ` · ${l.waterCapacityL} L` : ''}</option>)}
                </select>
              : <span className="muted">{k.orderLineId ? `line #${k.orderLineId}` : 'no line'}</span>;
          })() : null}
          <Button small kind="danger" onClick={() => setKept((old) => old.filter((x) => x.cylinderId !== k.cylinderId))}>Take it off</Button>
        </div>
      ))}
      <ScanBox label="Put another cylinder on" value={serial} onChange={setSerial} onAdd={() => { void add(); }}
               help="scan or type a serial and press Enter" />
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || kept.length === 0 || unchanged}>
        {runner.busy ? 'Saving…' : unchanged ? 'The load is as it was' : 'Save the corrected load'}
      </Button>
    </Card>
  );
}

/**
 * A CHARGE THAT IS NOT A CYLINDER. Freight, loading, a hose that went with the
 * load: it belongs on the memo the departure raises, so it is added while the
 * challan is still pending and never afterwards. Money is typed in rupees here
 * and sent in paise, because paise is what the door takes.
 */
function AddCharge({ challanId, onDone }: { challanId: number; onDone: () => void }) {
  const [chargeType, setChargeType] = useState('freight');
  const [description, setDescription] = useState('');
  const [qty, setQty] = useState('');
  const [rupees, setRupees] = useState('');
  const runner = useRun(MESSAGES);
  const paise = rupees.trim() ? rupeesToPaise(rupees) : undefined;

  async function submit() {
    const ok = await runner.run(() => api.command('challan.addPendingCharge', {
      challanId,
      chargeType: chargeType as 'loading_unloading' | 'freight' | 'services' | 'accessories',
      description: description.trim(),
      ...(qty.trim() ? { qty: Number(qty) } : {}),
      ...(paise !== undefined && paise !== null ? { amountPaise: paise } : {}),
    }));
    if (ok) onDone();
  }

  return (
    <Card title="Add a charge to this load" sub="it goes on the memo the departure raises — a charge cannot be added after that">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {paise === null ? <Banner kind="warn" title="That amount is not money." detail="Rupees, with at most two decimals." /> : null}
      <div className="grid two">
        <div>
          <Select name="charge-type" label="What kind of charge" value={chargeType} onChange={setChargeType}
                  options={[{ value: 'freight', label: 'Freight' }, { value: 'loading_unloading', label: 'Loading / unloading' },
                            { value: 'services', label: 'Services' }, { value: 'accessories', label: 'Accessories' }]} />
          <Text name="charge-description" label="What it is for" value={description} onChange={setDescription} required />
        </div>
        <div>
          <Text name="charge-qty" label="Quantity" type="number" value={qty} onChange={setQty} help="leave blank if it is a single amount" />
          <Text name="charge-amount" label="Amount (₹)" value={rupees} onChange={setRupees}
                help="leave blank to use the customer's agreed rate" />
        </div>
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !description.trim() || paise === null}>
        {runner.busy ? 'Adding…' : 'Add the charge'}
      </Button>
    </Card>
  );
}


/**
 * WHICH ORDER LINE A SCANNED CYLINDER FILLS (audit, 9 Oct 2026).
 *
 * This matched on gas alone and took the first line with anything
 * outstanding. Two lines for the same gas in different sizes — common on a
 * real order — put every cylinder on the first line, and the server then
 * refused a perfectly good 40 L shell because the screen had attached it to
 * the 46.7 L line. Every requirement the line states is matched now: the gas,
 * the size when the line names one, and something still outstanding. One fit
 * is taken; none is said in words; several are left for the operator to pick.
 */
interface LineLike { orderLineId: number; lineNo: number; gasService: string; waterCapacityL: string | null; qtyOutstanding: number }
function linesThatFit(lines: readonly LineLike[] | undefined, cyl: { gasService: string; waterCapacityL: string | null }): LineLike[] {
  return (lines ?? []).filter((l) => l.gasService === cyl.gasService && l.qtyOutstanding > 0
    && (l.waterCapacityL === null || cyl.waterCapacityL === null || Number(l.waterCapacityL) === Number(cyl.waterCapacityL)));
}

interface Scanned { cylinderId: number; internalId: string; serialNo: string; gasService: string; waterCapacityL: string | null; orderLineId?: number }

interface PrepareDraft { customerId: string; siteId: string; orderId: string; mode: 'our_truck' | 'customer_pickup'; vehicle: string; eventDate: string; withoutOrder: string; withoutOrderNote: string; queue: Scanned[] }
function prepareDraftKey(actorId: number): string { return `cts2_prepare_draft_${actorId}`; }
function readPrepareDraft(actorId: number): PrepareDraft | null {
  try {
    const raw = globalThis.localStorage?.getItem(prepareDraftKey(actorId));
    if (!raw) return null;
    const d = JSON.parse(raw) as PrepareDraft;
    return Array.isArray(d.queue) && d.queue.length > 0 ? d : null;
  } catch { return null; }
}

function PrepareDispatch({ session }: { session: Session }) {
  // The order screen sends the yard here with the order it is filling
  // (`#/dispatch/new?order=12`). The customer and the site are the order's, not
  // a second chance to pick the wrong ones.
  const asked = routeQuery('order');
  const [customerId, setCustomerId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [orderId, setOrderId] = useState(asked ?? '');
  const [mode, setMode] = useState<'our_truck' | 'customer_pickup'>('our_truck');
  const [vehicle, setVehicle] = useState('');
  const [eventDate, setEventDate] = useState(today());
  const [serial, setSerial] = useState('');
  const [queue, setQueue] = useState<Scanned[]>([]);
  const [withoutOrder, setWithoutOrder] = useState('');
  const [withoutOrderNote, setWithoutOrderNote] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  // A SCANNED LOAD SURVIVES A REFRESH (audit, 9 Oct 2026). The queue lived
  // only in screen memory, so a dropped tab after twenty scans was twenty
  // scans again. Saved per user — and per company, because localStorage is
  // per origin and the origin IS the company (ADR-010). On restore every
  // cylinder is resolved again and anything no longer filled and in the plant
  // is dropped with a word, because a draft is a memory and not a fact.
  const [savedDraft, setSavedDraft] = useState<PrepareDraft | null>(() => readPrepareDraft(session.actorId));
  useEffect(() => {
    try {
      if (queue.length === 0) { globalThis.localStorage?.removeItem(prepareDraftKey(session.actorId)); return; }
      globalThis.localStorage?.setItem(prepareDraftKey(session.actorId),
        JSON.stringify({ customerId, siteId, orderId, mode, vehicle, eventDate, withoutOrder, withoutOrderNote, queue } satisfies PrepareDraft));
    } catch { /* a private window forgets; the screen still works */ }
  }, [customerId, siteId, orderId, mode, vehicle, eventDate, withoutOrder, withoutOrderNote, queue, session.actorId]);
  async function restoreDraft(d: PrepareDraft) {
    setCustomerId(d.customerId); setSiteId(d.siteId); setOrderId(d.orderId); setMode(d.mode);
    setVehicle(d.vehicle); setEventDate(d.eventDate); setWithoutOrder(d.withoutOrder); setWithoutOrderNote(d.withoutOrderNote);
    const kept: Scanned[] = []; const dropped: string[] = [];
    for (const q of d.queue) {
      const r = await api.view('cylinders.resolve', { token: q.internalId });
      const now = r.candidates.find((c) => c.cylinderId === q.cylinderId);
      if (now && now.state === 'filled_in_plant') kept.push(q); else dropped.push(q.internalId);
    }
    setQueue(kept); setSavedDraft(null);
    if (dropped.length) setScanError(`Left off the restored load — no longer filled and in the plant: ${dropped.join(', ')}.`);
  }
  const runner = useRun(MESSAGES);

  const sites = useAsk(() => (customerId ? api.view('sites.list', { customerId: Number(customerId), active: true }) : Promise.resolve({ rows: [] })), [customerId], MESSAGES);
  const orders = useAsk(() => (customerId ? api.view('orders.list', { customerId: Number(customerId), status: 'approved', outstanding: true, pageSize: 50 }) : Promise.resolve({ rows: [], page: { number: 1, size: 0, total: 0 } })), [customerId], MESSAGES);
  const order = useAsk(() => (orderId ? api.view('orders.get', { orderId: Number(orderId) }) : Promise.resolve(null)), [orderId], MESSAGES);

  // When the yard arrives with an order named, the order says whose it is.
  useEffect(() => {
    if (!asked) return;
    let live = true;
    api.view('orders.get', { orderId: Number(asked) }).then(
      (d) => { if (live) { setCustomerId(String(d.order.customerId)); setSiteId(String(d.order.siteId)); } },
      () => { /* the order register will say why; the screen still works by hand */ },
    );
    return () => { live = false; };
  }, [asked]);

  /** A scanned serial is looked up before it joins the queue: the yard is told at the counter, not after the lorry has gone. */
  async function addScanned() {
    const typed = serial.trim();
    if (!typed) return;
    setScanError(null);
    try {
      // IDENTITY IS THE SERVER'S ANSWER, BY EQUALITY, OVER THE WHOLE REGISTER.
      // This asked the SUBSTRING search for five rows and, when none of them
      // matched exactly, took `?? found.rows[0]` — a different cylinder, put
      // on the challan silently. It also could not tell "no such cylinder"
      // from "the exact one was not in the five I asked for", because that
      // page is ordered by internal id, not by how well a row answers.
      const r = await api.view('cylinders.resolve', { token: typed });
      if (r.total === 0) { setScanError(`No cylinder answers to “${typed}”.`); return; }
      if (r.total > 1) {
        setScanError(r.collision
          ? `“${typed}” is one cylinder's own number and another's sticker — add it from the yard screen, where you can pick between them.`
          : `“${typed}” is on ${r.total} different cylinders — add it from the yard screen, where you can pick between them.`);
        return;
      }
      const hit = r.candidates[0]!;
      if (queue.some((q) => q.cylinderId === hit.cylinderId)) { setScanError(`${hit.internalId} is already on this load.`); return; }
      if (hit.state !== 'filled_in_plant') { setScanError(`${hit.internalId} is ${words(hit.state)} — only a filled cylinder in the plant can go out.`); return; }
      const fits = linesThatFit(order.data?.lines, hit);
      if (orderId && fits.length === 0) { setScanError(`${hit.internalId} fits no line of this order that still has anything outstanding — check the gas and the size.`); return; }
      const line = fits.length === 1 ? fits[0] : undefined;
      setQueue((old) => [...old, { cylinderId: hit.cylinderId, internalId: hit.internalId, serialNo: hit.serialNo, gasService: hit.gasService,
                                   waterCapacityL: hit.waterCapacityL, ...(line ? { orderLineId: line.orderLineId } : {}) }]);
      setSerial('');
    } catch (e) {
      setScanError(e instanceof Error ? e.message : String(e));
    }
  }

  async function submit() {
    const ok = await runner.run(async () => {
      const r = await api.command('challan.createDispatch', {
        customerId: Number(customerId), siteId: Number(siteId), eventDate, deliveryMode: mode,
        ...(vehicle.trim() ? { vehicle: vehicle.trim() } : {}),
        ...(orderId ? { orderId: Number(orderId) } : {}),
        ...(!orderId && withoutOrder ? { withoutOrder: { reason: withoutOrder as 'walk_in' | 'replacement' | 'emergency' | 'legacy', note: withoutOrderNote.trim() } } : {}),
        lines: queue.map((q) => ({ cylinderId: q.cylinderId, ...(q.orderLineId ? { orderLineId: q.orderLineId } : {}) })),
      });
      const value = r.value as { challanId?: number };
      if (value?.challanId) go(`#/dispatch/${value.challanId}`);
      return r;
    });
    if (!ok) return;
  }

  const ready = customerId && siteId && queue.length > 0 && (orderId || (withoutOrder && withoutOrderNote.trim().length >= 10));

  return (
    <Page title="Prepare a dispatch" sub="Scan or type each cylinder. Nothing moves until somebody checks and approves the load.">
      {savedDraft ? (
        <Card title={`Unfinished load — ${count(savedDraft.queue.length)} cylinder(s) scanned and not sent`}
              sub="Restore it to carry on, or discard it. Every cylinder is checked again on restore.">
          <div className="toolbar">
            <Button kind="primary" onClick={() => { void restoreDraft(savedDraft); }}>Restore the load</Button>
            <Button onClick={() => { setSavedDraft(null); try { globalThis.localStorage?.removeItem(prepareDraftKey(session.actorId)); } catch { /* gone is gone */ } }}>Discard it</Button>
          </div>
        </Card>
      ) : null}
      <div className="grid two">
        <Card title="The load">
          {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
          {asked && order.data ? (
            <p className="muted" style={{ marginTop: 0 }}>Filling the order for <strong>{order.data.order.customerName}</strong> · {order.data.order.siteName} · {count(order.data.order.qtyOutstanding)} still to send · wanted by {date(order.data.order.requestedDate)}</p>
          ) : null}
          <CustomerPickerById name="customer" label="Which customer?" customerId={customerId}
                              onPick={(v) => { setCustomerId(v); setSiteId(''); setOrderId(''); setQueue([]); }} placeholder="Search or select customer…" />
          <Select name="site" label="Site" value={siteId} onChange={setSiteId} placeholder={customerId ? 'Choose a site' : 'Choose the customer first'}
                  options={(sites.data?.rows ?? []).map((s) => ({ value: String(s.siteId), label: s.name + (s.isPrimary ? ' (primary)' : '') }))} />
          <Select name="order" label="Order being filled" value={orderId} onChange={setOrderId}
                  placeholder="No order — this is a direct challan"
                  help="a load prepared from an approved order is priced by the order's approved rates"
                  options={(orders.data?.rows ?? []).map((o) => ({ value: String(o.orderId), label: `#${o.orderId} · ${count(o.qtyOutstanding)} outstanding · wanted ${date(o.requestedDate)}` }))} />

          {!orderId ? (
            <>
              <Select name="without-order" label="Why is there no order?" value={withoutOrder} onChange={setWithoutOrder} placeholder="Choose the reason"
                      options={[{ value: 'walk_in', label: 'Walk-in customer' }, { value: 'replacement', label: 'Replacement' }, { value: 'emergency', label: 'Emergency' }, { value: 'legacy', label: 'Legacy / old register entry' }]} />
              <TextArea name="without-order-note" label="Say what happened" value={withoutOrderNote} onChange={setWithoutOrderNote}
                        help="a few words, at least ten characters — this is the record of why there was no order" />
            </>
          ) : null}

          <Text name="event-date" label="Date" type="date" value={eventDate} onChange={setEventDate} required />
          <Select name="mode" label="Delivery" value={mode} onChange={(v) => setMode(v === 'customer_pickup' ? 'customer_pickup' : 'our_truck')}
                  options={[{ value: 'our_truck', label: 'Our truck' }, { value: 'customer_pickup', label: 'Customer collects' }]} />
          <Text name="vehicle" label="Truck / vehicle no (optional)" value={vehicle} onChange={setVehicle} placeholder="e.g. WB-26-A-1234" />

          {scanError ? <Banner kind="warn" title={scanError} /> : null}
          <ScanBox label="Cylinder serial" value={serial} onChange={setSerial} onAdd={() => { void addScanned(); }}
                   help="scan or type a serial and press Enter" />

          <Button kind="primary" wide disabled={!ready || runner.busy} onClick={() => { void submit(); }}>
            {runner.busy ? 'Recording…' : queue.length === 0 ? 'Add cylinders first' : `Prepare the challan (${count(queue.length)})`}
          </Button>
          <p className="muted" style={{ textAlign: 'center', marginBottom: 0 }}>
            Cylinders move only after the load is approved. Until then they stay where they are.
          </p>
        </Card>

        <div>
          <Card title="On this load" {...(queue.length ? { sub: `${count(queue.length)} cylinder(s)` } : {})}>
            {queue.length === 0 ? (
              <div className="queue empty">
                <strong>Queue is empty</strong>
                <span>Scan or type a cylinder serial on the left.<br />Each one you add appears here.</span>
              </div>
            ) : (
              <div>
                {queue.map((q) => (
                  <div className="queue-item" key={q.cylinderId}>
                    <span className="grow"><Serial>{q.internalId}</Serial> <span className="muted">{q.serialNo} · {words(q.gasService)}</span></span>
                    {orderId ? (() => {
            const fits = linesThatFit(order.data?.lines, q);
            return fits.length > 1 || (!q.orderLineId && fits.length > 0)
              ? <select value={q.orderLineId ?? ''} aria-label="Which order line this cylinder fills"
                        onChange={(e: { target: { value: string } }) => setQueue((old) => old.map((x) => { if (x.cylinderId !== q.cylinderId) return x; const { orderLineId: _was, ...rest } = x; const v = Number(e.target.value); return v ? { ...rest, orderLineId: v } : rest; }))}>
                  <option value="">Which line?</option>
                  {fits.map((l) => <option key={l.orderLineId} value={l.orderLineId}>line #{l.lineNo} · {words(l.gasService)}{l.waterCapacityL ? ` · ${l.waterCapacityL} L` : ''}</option>)}
                </select>
              : <span className="muted">{q.orderLineId ? `line #${q.orderLineId}` : 'no line'}</span>;
          })() : null}
                    <Button small kind="danger" onClick={() => setQueue((old) => old.filter((x) => x.cylinderId !== q.cylinderId))}>Remove</Button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {order.data ? (
            <Card title={`Order #${order.data.order.orderId}`} sub="what this load is filling">
              <Register
                columns={[
                  { key: 'line', head: 'Line', cell: (l: { lineNo: number; orderLineId: number }) => `#${l.lineNo}` },
                  { key: 'gas', head: 'Gas', cell: (l: { gasService: string; waterCapacityL: string | null }) => <>{words(l.gasService)}{l.waterCapacityL ? <span className="muted"> · {l.waterCapacityL} L</span> : null}</> },
                  { key: 'outstanding', head: 'Still to send', right: true, cell: (l: { qtyOutstanding: number; qtyUnit: string }) => qtyText(l.qtyOutstanding, l.qtyUnit) },
                  { key: 'here', head: 'On this load', right: true, cell: (l: { orderLineId: number }) => count(queue.filter((q) => q.orderLineId === l.orderLineId).length) },
                ]}
                rows={order.data.lines} empty="This order has no lines." />
            </Card>
          ) : null}
        </div>
      </div>
    </Page>
  );
}
